import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TableContainer,
  Toolbar,
  Tooltip,
} from '@mui/material';
import { TaskFavoritePydantic as TaskFavorite, TaskState } from 'api-client';
import React from 'react';
import {
  FilterFields,
  MuiMouseEvent,
  SortFields,
  TaskDataGridTable,
  Tasks,
  Window,
  // Add task
  CreateTaskForm,
  CreateTaskFormProps,
  getPlacesbyLevel,
} from 'react-components';
import { Subscription } from 'rxjs';
import { AppControllerContext } from '../app-contexts';
import { AppEvents } from '../app-events';
import { MicroAppProps } from '../micro-app';
import { RmfAppContext } from '../rmf-app';
import { TaskSummary } from './task-summary';
import { downloadCsvFull, downloadCsvMinimal } from './utils';
import { UserProfileContext } from 'rmf-auth';
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  selectedTabIndex: number;
}

function tabId(index: number): string {
  return `simple-tab-${index}`;
}

function tabPanelId(index: number): string {
  return `simple-tabpanel-${index}`;
}

function TabPanel(props: TabPanelProps) {
  const { children, selectedTabIndex, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={selectedTabIndex !== index}
      id={tabPanelId(index)}
      aria-labelledby={tabId(index)}
      {...other}
    >
      {selectedTabIndex === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Generates a list of ProcessedEvents to occur within the query start and end,
 * based on the provided schedule.
 * @param start The start of the query, which is generally 00:00:00 of the first
 * day in the calendar view.
 * @param end The end of the query, which is generally 23:59:59 of the last day
 * in the calendar view.
 * @param schedule The current schedule, to be checked if there are any events
 * between start and end.
 * @param getEventId Callback function to get the event ID.
 * @param getEventTitle Callback function to get the event title.
 * @returns List of ProcessedEvents to occur within the query start and end.
 */

export const FilteredTasksApp = React.memo(
  React.forwardRef(
    (
      { onClose, children, ...otherProps }: React.PropsWithChildren<MicroAppProps>,
      ref: React.Ref<HTMLDivElement>,
    ) => {
      const rmf = React.useContext(RmfAppContext);
      const [refreshTaskAppCount, setRefreshTaskAppCount] = React.useState(0);

      const uploadFileInputRef = React.useRef<HTMLInputElement>(null);
      const [openTaskSummary, setOpenTaskSummary] = React.useState(false);
      const [selectedTask, setSelectedTask] = React.useState<TaskState | null>(null);
      // add task
      const [openCreateTaskForm, setOpenCreateTaskForm] = React.useState(false);
      const [username, setUsername] = React.useState<string | null>(null);
      const [placeNames, setPlaceNames] = React.useState<string[]>([]);
      const [favoritesTasks, setFavoritesTasks] = React.useState<TaskFavorite[]>([]);
      const { showAlert } = React.useContext(AppControllerContext);

      const [tasksState, setTasksState] = React.useState<Tasks>({
        isLoading: true,
        data: [],
        total: 0,
        page: 1,
        pageSize: 10,
      });

      const [filterFields, setFilterFields] = React.useState<FilterFields>({ model: undefined });
      const [sortFields, setSortFields] = React.useState<SortFields>({ model: undefined });

      React.useEffect(() => {
        const sub = AppEvents.refreshTaskAppCount.subscribe((currentValue) => {
          setRefreshTaskAppCount(currentValue);
        });
        return () => sub.unsubscribe();
      }, []);

      // TODO: parameterize this variable
      const GET_LIMIT = 10;
      React.useEffect(() => {
        if (!rmf) {
          return;
        }

        let filterColumn: string | undefined = undefined;
        let filterValue: string | undefined = undefined;
        if (filterFields.model && filterFields.model.items.length >= 1) {
          filterColumn = filterFields.model.items[0].columnField;
          filterValue = filterFields.model.items[0].value;
          console.log(filterColumn, filterValue);
          const filterOperator: string | undefined = filterFields.model.items[0].operatorValue;
          if (
            (filterColumn === 'unix_millis_start_time' ||
              filterColumn === 'unix_millis_finish_time') &&
            filterValue
          ) {
            const selectedTime = new Date(filterValue);
            if (filterOperator && filterOperator === 'onOrBefore') {
              filterValue = `0,${selectedTime.getTime()}`;
            } else if (filterOperator && filterOperator === 'onOrAfter') {
              // Enforce an upper limit which is 24 hours ahead of the current time
              const now = new Date();
              const upperLimit = now.getTime() + 86400000;
              filterValue = `${selectedTime.getTime()},${upperLimit}`;
            }
          }
        }

        let orderBy: string = '-unix_millis_start_time';
        if (sortFields.model && sortFields.model.length >= 1) {
          orderBy =
            sortFields.model[0].sort === 'desc'
              ? '-' + sortFields.model[0].field
              : sortFields.model[0].field;
        }

        const subs: Subscription[] = [];
        (async () => {
          const resp = await rmf.tasksApi.queryTaskStatesTasksGet(
            filterColumn && filterColumn === 'id_' ? filterValue : undefined,
            filterColumn && filterColumn === 'category' ? filterValue : undefined,
            filterColumn && filterColumn === 'assigned_to' ? filterValue : undefined,
            filterColumn && filterColumn === 'status' ? filterValue : undefined,
            filterColumn && filterColumn === 'unix_millis_start_time' ? filterValue : undefined,
            filterColumn && filterColumn === 'unix_millis_finish_time' ? filterValue : undefined,
            GET_LIMIT,
            (tasksState.page - 1) * GET_LIMIT, // Datagrid component need to start in page 1. Otherwise works wrong
            orderBy,
            undefined,
          );

          const results = resp.data as TaskState[];
          // const newTasks = results.slice(0, GET_LIMIT);
          // filtering added
          const filteredResults = results.filter((task) => task.assigned_to?.group.includes('aes'));
          const newTasks = filteredResults.slice(0, GET_LIMIT);

          setTasksState((old) => ({
            ...old,
            isLoading: false,
            data: newTasks,
            total:
              results.length === GET_LIMIT
                ? tasksState.page * GET_LIMIT + 1
                : tasksState.page * GET_LIMIT - 9,
          }));

          subs.push(
            ...newTasks.map((task) =>
              rmf
                .getTaskStateObs(task.booking.id)
                .subscribe((task) =>
                  setTasksState((prev) => ({ ...prev, [task.booking.id]: task })),
                ),
            ),
          );

          try {
            const user = (await rmf.defaultApi.getUserUserGet()).data;
            setUsername(user.username);
            console.log(`username: ${user.username}`);
          } catch (e) {
            console.log(`error getting username: ${(e as Error).message}`);
          }
          subs.push(
            rmf.buildingMapObs.subscribe((map) => {
              // Hardcoded for AES >> Deployment at L2, NavGraph 5 was assigned.
              const places = getPlacesbyLevel(map, 'L2');
              const placeNames = places
                .filter((p) => p.level === '5') // Filter by level
                .map((p) => p.vertex.name);
              console.log(placeNames);
              setPlaceNames(placeNames);
              // setPlaceNames(getPlacesbyLevel(map, "L2").map((p) => p.vertex.name)),
            }),
          );
        })();
        return () => subs.forEach((s) => s.unsubscribe());
      }, [rmf, refreshTaskAppCount, tasksState.page, filterFields.model, sortFields.model]);

      //#region 'Favorite Task'
      React.useEffect(() => {
        if (!rmf) {
          return;
        }
        (async () => {
          const resp = await rmf.tasksApi.getFavoritesTasksFavoriteTasksGet();

          const results = resp.data as TaskFavorite[];
          console.log(`Favourite task: ${results}`);
          if (results) {
            setFavoritesTasks(results);
          }
        })();

        return () => {
          setFavoritesTasks([]);
        };
      }, [rmf, refreshTaskAppCount]);

      const submitFavoriteTask = React.useCallback<
        Required<CreateTaskFormProps>['submitFavoriteTask']
      >(
        async (taskFavoriteRequest) => {
          if (!rmf) {
            throw new Error('tasks api not available');
          }
          await rmf.tasksApi.postFavoriteTaskFavoriteTasksPost(taskFavoriteRequest);
          AppEvents.refreshTaskAppCount.next(refreshTaskAppCount + 1);
        },
        [rmf, refreshTaskAppCount],
      );

      const deleteFavoriteTask = React.useCallback<
        Required<CreateTaskFormProps>['deleteFavoriteTask']
      >(
        async (favoriteTask) => {
          if (!rmf) {
            throw new Error('tasks api not available');
          }
          if (!favoriteTask.id) {
            throw new Error('Id is needed');
          }

          await rmf.tasksApi.deleteFavoriteTaskFavoriteTasksFavoriteTaskIdDelete(favoriteTask.id);
          AppEvents.refreshTaskAppCount.next(refreshTaskAppCount + 1);
        },
        [rmf, refreshTaskAppCount],
      );
      //#endregion 'Favorite Task'

      const getAllTasks = async (timestamp: Date) => {
        if (!rmf) {
          return [];
        }

        const resp = await rmf.tasksApi.queryTaskStatesTasksGet(
          undefined,
          undefined,
          undefined,
          undefined,
          `0,${timestamp.getTime()}`,
          undefined,
          undefined,
          undefined,
          '-unix_millis_start_time',
          undefined,
        );
        const allTasks = resp.data as TaskState[];
        return allTasks;
      };

      const exportTasksToCsv = async (minimal: boolean) => {
        const now = new Date();
        const allTasks = await getAllTasks(now);
        if (!allTasks || !allTasks.length) {
          return;
        }
        if (minimal) {
          downloadCsvMinimal(now, allTasks);
        } else {
          downloadCsvFull(now, allTasks);
        }
      };

      const [anchorExportElement, setAnchorExportElement] = React.useState<null | HTMLElement>(
        null,
      );
      const openExportMenu = Boolean(anchorExportElement);
      const handleClickExportMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorExportElement(event.currentTarget);
      };
      const handleCloseExportMenu = () => {
        setAnchorExportElement(null);
      };

      const submitTasks = React.useCallback<Required<CreateTaskFormProps>['submitTasks']>(
        async (taskRequests) => {
          if (!rmf) {
            throw new Error('tasks api not available');
          }
          await Promise.all(
            taskRequests.map((request) =>
              rmf.tasksApi.postDispatchTaskTasksDispatchTaskPost({
                type: 'dispatch_task_request',
                request,
              }),
            ),
          );

          AppEvents.refreshTaskAppCount.next(refreshTaskAppCount + 1);
        },
        [rmf, refreshTaskAppCount],
      );

      const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);
      const handleChange = (_: React.SyntheticEvent, newSelectedTabIndex: number) => {
        setSelectedTabIndex(newSelectedTabIndex);
      };

      const profile = React.useContext(UserProfileContext);
      console.log(profile);

      return (
        <Window
          ref={ref}
          title="Filtered Tasks"
          onClose={onClose}
          toolbar={
            <Toolbar variant="dense">
              <div>
                <Tooltip title="Download" placement="top">
                  <IconButton
                    id="export-button"
                    aria-controls={openExportMenu ? 'export-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={openExportMenu ? 'true' : undefined}
                    onClick={handleClickExportMenu}
                    color="inherit"
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  id="export-menu"
                  MenuListProps={{
                    'aria-labelledby': 'export-button',
                  }}
                  anchorEl={anchorExportElement}
                  open={openExportMenu}
                  onClose={handleCloseExportMenu}
                >
                  <MenuItem
                    onClick={() => {
                      handleCloseExportMenu();
                      exportTasksToCsv(true);
                    }}
                    disableRipple
                  >
                    Export Minimal
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleCloseExportMenu();
                      exportTasksToCsv(false);
                    }}
                    disableRipple
                  >
                    Export Full
                  </MenuItem>
                </Menu>
              </div>
              <Tooltip title="Refresh" color="inherit" placement="top">
                <IconButton
                  onClick={() => {
                    AppEvents.refreshTaskAppCount.next(refreshTaskAppCount + 1);
                  }}
                  aria-label="Refresh"
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Create task" color="inherit" placement="top">
                <IconButton onClick={() => setOpenCreateTaskForm(true)} aria-label="Create Task">
                  <AddOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Toolbar>
          }
          {...otherProps}
        >
          <Tabs value={selectedTabIndex} onChange={handleChange} aria-label="Task App Tabs">
            <Tab label="Queue" id={tabId(0)} aria-controls={tabPanelId(0)} />
          </Tabs>
          <TabPanel selectedTabIndex={selectedTabIndex} index={0}>
            <TableContainer>
              <TaskDataGridTable
                tasks={tasksState}
                onTaskClick={(_: MuiMouseEvent, task: TaskState) => {
                  setSelectedTask(task);
                  setOpenTaskSummary(true);
                }}
                setFilterFields={setFilterFields}
                setSortFields={setSortFields}
                onPageChange={(newPage: number) =>
                  setTasksState((old: Tasks) => ({ ...old, page: newPage + 1 }))
                }
                onPageSizeChange={(newPageSize: number) =>
                  setTasksState((old: Tasks) => ({ ...old, pageSize: newPageSize }))
                }
              />
            </TableContainer>
          </TabPanel>
          <input type="file" style={{ display: 'none' }} ref={uploadFileInputRef} />
          {openTaskSummary && (
            <TaskSummary task={selectedTask} onClose={() => setOpenTaskSummary(false)} />
          )}
          {openCreateTaskForm && (
            <CreateTaskForm
              user={username ? username : 'unknown user'}
              cleaningZones={placeNames}
              loopWaypoints={placeNames}
              deliveryWaypoints={placeNames}
              dispensers={[]}
              ingestors={[]}
              favoritesTasks={favoritesTasks}
              open={openCreateTaskForm}
              onClose={() => setOpenCreateTaskForm(false)}
              submitTasks={submitTasks}
              submitFavoriteTask={submitFavoriteTask}
              deleteFavoriteTask={deleteFavoriteTask}
              onSuccess={() => {
                setOpenCreateTaskForm(false);
                showAlert('success', 'Successfully created task');
              }}
              onFail={(e) => {
                showAlert('error', `Failed to create task: ${e.message}`);
              }}
              onSuccessFavoriteTask={(message) => {
                showAlert('success', message);
              }}
              onFailFavoriteTask={(e) => {
                showAlert('error', `Failed to create or delete favorite task: ${e.message}`);
              }}
            />
          )}
          {children}
        </Window>
      );
    },
  ),
);
